import { AxiosApiError, getApiError } from '@/types/apiError';
import { CancelledError, QueryCacheNotifyEvent, QueryFunction, QueryKey, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

type DataGridResponse<T> = {
  data: T;
};

type ReturnType<T, R> = R extends undefined ? T : R;

export const useDataGridFetcher = <
  T extends { pageData: { total: number } },
  R = undefined,
  P extends { page?: string | number; perPage?: string | number } = {
    page?: string | number;
    perPage?: string | number;
  },
>({
  queryKey,
  queryFn,
  transformResponse,
  onError,
  enabled = true,
}: {
  queryKey: (parameters?: P) => QueryKey;
  queryFn: (parameters?: P) => QueryFunction<DataGridResponse<T>>;
  transformResponse?: (data: T) => R;
  onError?: (error: Error) => void;
  enabled?: boolean;
}) => {
  const queryClient = useQueryClient();
  const [rawError, setRawError] = useState<AxiosApiError>();
  const [resetTrigger, setResetTrigger] = useState(0);

  const queryKeyReference = useRef(queryKey);
  queryKeyReference.current = queryKey;
  const queryFunctionReference = useRef(queryFn);
  queryFunctionReference.current = queryFn;
  const transformResponseReference = useRef(transformResponse);
  transformResponseReference.current = transformResponse;

  useEffect(() => {
    const unsubscribe = queryClient
      .getQueryCache()
      .subscribe((event: QueryCacheNotifyEvent & { action?: { type: string; manual?: boolean } }) => {
        const actionType = event.action?.type;
        const isReset = actionType === 'setState';
        const isManualSet = actionType === 'success' && event.action?.manual === true;
        if (!isReset && !isManualSet) return;

        const baseQueryKey = queryKeyReference.current({
          page: undefined,
          perPage: undefined,
        } as P);

        const removedQueryKey = event.query.queryKey;

        if (
          Array.isArray(removedQueryKey) &&
          Array.isArray(baseQueryKey) &&
          removedQueryKey[0] === baseQueryKey[0] &&
          removedQueryKey[1] &&
          baseQueryKey[1] &&
          typeof removedQueryKey[1] === 'object' &&
          typeof baseQueryKey[1] === 'object'
        ) {
          const removedParameters = removedQueryKey[1] as Record<string, unknown>;
          const baseParameters = baseQueryKey[1] as Record<string, unknown>;

          const isSameBaseQuery = Object.keys(baseParameters).every(key => {
            if (key === 'page' || key === 'perPage') return true;
            return removedParameters[key] === baseParameters[key];
          });

          if (isSameBaseQuery) {
            setResetTrigger(previous => previous + 1);
          }
        }
      });

    return unsubscribe;
  }, [queryClient]);

  useEffect(() => {
    if (rawError && onError) {
      onError(rawError);
    }
  }, [rawError, onError]);

  const fetchData = useCallback(
    async (parameters?: P): Promise<ReturnType<T, R>> => {
      if (!enabled) {
        return {
          pageData: { total: 0 },
        } as ReturnType<T, R>;
      }

      try {
        clearError();
        const response = await queryClient.ensureQueryData({
          queryKey: queryKeyReference.current(parameters),
          queryFn: queryFunctionReference.current(parameters),
          retry: false,
        });
        if (transformResponseReference.current) {
          return transformResponseReference.current(response.data) as ReturnType<T, R>;
        }

        return response.data as ReturnType<T, R>;
      } catch (error) {
        if (!(error instanceof CancelledError)) {
          setRawError(error as AxiosApiError);
        }
        throw error;
      }
    },
    [queryClient, resetTrigger, enabled]
  );

  const clearError = useCallback(() => {
    setRawError(undefined);
  }, []);

  return {
    fetchData,
    error: getApiError(rawError),
    clearError,
  };
};
